import { useNavigate } from 'react-router-dom';
import { DataTable, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Badge, Button, Group, Text } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDate, formatRelative } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { TenantName } from '../../../components/TenantName';
import { TwoLineCell } from '../../../components/TwoLineCell';
// The roles page is the users list with one privilege control bolted on, so it
// shares the users page's avatar rather than drawing a second one.
import { PersonAvatar } from '../../users/components/PersonAvatar';
import { useT } from '../../../i18n/I18nProvider';

interface Props {
  records: admin.AdminUser[];
  loading: boolean;
  pendingId?: string;
  currentUserId?: string;
  onPromote: (u: admin.AdminUser) => void;
  onDemote: (u: admin.AdminUser) => void;
}

export function RolesTable({ records, loading, pendingId, currentUserId, onPromote, onDemote }: Props) {
  const t = useT();
  const navigate = useNavigate();
  const unnamed = t('users.unnamed');

  return (
    <DataTable<Indexed<admin.AdminUser>>
      records={records as Indexed<admin.AdminUser>[]}
      loading={loading}
      idAccessor="id"
      emptyState={t('users.empty')}
      onRowClick={(r) => navigate(`/users/${r.id}`)}
      columns={[
        {
          accessor: 'display_name',
          title: t('roles.columns.user'),
          sortable: true,
          render: (r) => (
            <TwoLineCell
              primary={
                // A span, not a Group: `primary` is rendered inside a
                // paragraph, and a div in a <p> is closed early by the browser.
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <span>{r.display_name || r.email || unnamed}</span>
                  {/* Knowing which row is your own is what stops an accidental self-demotion. */}
                  {r.id === currentUserId && <Badge size="xs" variant="outline">{t('roles.you')}</Badge>}
                </span>
              }
              secondary={r.email ?? t('users.noEmail')}
              title={r.email ?? undefined}
              leading={<PersonAvatar name={r.display_name || r.email || unnamed} />}
            />
          )
        },
        {
          accessor: 'organisation_id',
          title: t('roles.filters.organisation'),
          render: (r) => <TenantName id={r.organisation_id} />
        },
        {
          accessor: 'scope',
          title: t('users.filters.scope'),
          sortable: true,
          render: (r) => (
            <StatusBadge size="xs" status={r.scope === 'platform' ? 'warning' : 'pending'}>
              {r.scope === 'platform' ? t('users.scope.platform') : t('users.scope.tenant')}
            </StatusBadge>
          )
        },
        {
          accessor: 'created_at',
          title: t('roles.columns.joined'),
          sortable: true,
          render: (r) => <TwoLineCell primary={formatDate(r.created_at)} secondary={formatRelative(r.created_at)} />
        },
        {
          accessor: 'actions',
          title: t('common.actions'),
          textAlign: 'right',
          render: (r) => (
            <Group gap="xs" wrap="nowrap" justify="flex-end" onClick={(e) => e.stopPropagation()}>
              {r.scope === 'tenant' && (
                <Button size="xs" variant="outline" loading={pendingId === r.id} onClick={() => onPromote(r)}>
                  {t('roles.promote')}
                </Button>
              )}
              {r.scope === 'platform' && (
                r.id === currentUserId ? (
                  <Tooltip label={t('roles.selfDemotionBlocked')}>
                    <span style={{ display: 'inline-flex' }}>
                      <Button size="xs" variant="subtle" disabled>{t('roles.demote')}</Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Button size="xs" variant="subtle" loading={pendingId === r.id} onClick={() => onDemote(r)}>
                    {t('roles.demote')}
                  </Button>
                )
              )}
              {r.scope !== 'tenant' && r.scope !== 'platform' && (
                <Text role="caption" c="var(--rr-color-text-muted)">{t('common.notAvailable')}</Text>
              )}
            </Group>
          )
        }
      ]}
    />
  );
}
